import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useObraStore } from '@stores/obraStore.js'
import { exportarBackup, importarBackup, getDataUltimoBackup } from '@services/backup.js'
import {
  saveConfiguracoes,
  resetarProgressoModulo,
  resetarTodosProgresso,
  limparTodosDados,
} from '@services/crud.js'
import { ROUTES, MODULOS } from '@utils/constants.js'
import type { ObraConfig } from '@services/db.js'
import { configToForm, type EditForm } from './types.js'

export function useConfigPage() {
  const config = useObraStore((s) => s.config)
  const { save } = useObraStore()
  const navigate = useNavigate()

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [errors, setErrors] = useState<Partial<Record<keyof EditForm, string>>>({})
  const [importando, setImportando] = useState(false)
  const [confirmImport, setConfirmImport] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [backupMsg, setBackupMsg] = useState<string | null>(null)
  const [ultimoBackup, setUltimoBackup] = useState<string | null>(getDataUltimoBackup)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [valorMinimoNF, setValorMinimoNF] = useState(() => String(config?.valorMinimoNF ?? ''))
  const [dataAlvara, setDataAlvara] = useState(() => config?.dataAlvara ?? '')
  const [reservaPct, setReservaPct] = useState(() =>
    config?.reservaContingenciaPct != null ? String(config.reservaContingenciaPct) : '',
  )
  const [dataPrevisaoTermino, setDataPrevisaoTermino] = useState(
    () => config?.dataPrevisaoTermino ?? '',
  )
  const [savingConf, setSavingConf] = useState(false)
  const [savedConf, setSavedConf] = useState(false)

  const [moduloReset, setModuloReset] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const [resettingProg, setResettingProg] = useState(false)
  const [resetMsg, setResetMsg] = useState<string | null>(null)

  const [confirmLimpar, setConfirmLimpar] = useState(false)
  const [confirmLimpar2, setConfirmLimpar2] = useState(false)
  const [digitouNome, setDigitouNome] = useState('')
  const [limpando, setLimpando] = useState(false)

  async function handleExportarBackup() {
    await exportarBackup()
    setUltimoBackup(new Date().toISOString())
    setBackupMsg('Backup exportado com sucesso!')
    setTimeout(() => setBackupMsg(null), 4000)
  }

  function handleSelecionarImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setImportFile(file)
      setConfirmImport(true)
    }
  }

  async function handleConfirmarImport() {
    if (!importFile) return
    setImportando(true)
    try {
      await importarBackup(importFile)
      setConfirmImport(false)
      setImportFile(null)
      window.location.reload()
    } catch {
      setBackupMsg('Erro ao importar backup. Verifique o arquivo e tente novamente.')
      setConfirmImport(false)
    } finally {
      setImportando(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function startEdit() {
    if (!config) return
    setEditForm(configToForm(config))
    setErrors({})
    setSaved(false)
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setEditForm(null)
    setErrors({})
  }

  function setOrcamentoTotal(value: number) {
    setEditForm((f) => (f ? { ...f, orcamentoTotal: value } : f))
    setErrors((er) => ({ ...er, orcamentoTotal: '' }))
  }

  function setField(field: keyof EditForm) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const val = field === 'areaTotal' ? Number(e.target.value) || 0 : e.target.value
      setEditForm((f) => (f ? { ...f, [field]: val } : f))
      setErrors((er) => ({ ...er, [field]: '' }))
    }
  }

  function validate(): boolean {
    if (!editForm) return false
    const errs: Partial<Record<keyof EditForm, string>> = {}
    if (!editForm.nome.trim()) errs.nome = 'Informe o nome da obra'
    if (!editForm.areaTotal) errs.areaTotal = 'Informe a área total'
    if (!editForm.padraoConstrucao) errs.padraoConstrucao = 'Selecione o padrão'
    if (!editForm.orcamentoTotal) errs.orcamentoTotal = 'Informe o orçamento'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSave() {
    if (!validate() || !editForm) return
    setSaving(true)
    try {
      await save(editForm)
      setEditing(false)
      setEditForm(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveConfiguracoes() {
    setSavingConf(true)
    try {
      const reserva = reservaPct === '' ? NaN : Number(reservaPct)
      await saveConfiguracoes({
        valorMinimoNF: valorMinimoNF ? Number(valorMinimoNF) : undefined,
        dataAlvara: dataAlvara || undefined,
        reservaContingenciaPct: Number.isFinite(reserva)
          ? Math.min(Math.max(Math.round(reserva), 0), 50)
          : undefined,
        dataPrevisaoTermino: dataPrevisaoTermino || undefined,
      })
      setSavedConf(true)
      setTimeout(() => setSavedConf(false), 3000)
    } finally {
      setSavingConf(false)
    }
  }

  async function handleResetarProgresso() {
    setResettingProg(true)
    try {
      if (moduloReset) {
        await resetarProgressoModulo(moduloReset)
        const nome = MODULOS.find((m) => m.id === moduloReset)?.titulo ?? moduloReset
        setResetMsg(`Progresso do módulo "${nome}" reiniciado.`)
      } else {
        await resetarTodosProgresso()
        setResetMsg('Progresso de todos os módulos reiniciado.')
      }
      setConfirmReset(false)
      setTimeout(() => setResetMsg(null), 4000)
    } finally {
      setResettingProg(false)
    }
  }

  async function handleLimparTodos() {
    setLimpando(true)
    try {
      await limparTodosDados()
      useObraStore.setState({ config: null, isLoaded: true, isOnboarded: false })
      navigate(ROUTES.ONBOARDING, { replace: true })
    } finally {
      setLimpando(false)
    }
  }

  function cancelImport() {
    setConfirmImport(false)
    setImportFile(null)
  }

  function openLimpar() {
    setConfirmLimpar(true)
    setDigitouNome('')
  }

  function closeLimparStep1() {
    setConfirmLimpar(false)
  }

  function closeLimparStep2() {
    setConfirmLimpar(false)
    setConfirmLimpar2(false)
    setDigitouNome('')
  }

  return {
    config: config as ObraConfig | null,
    saved,
    editing,
    saving,
    editForm,
    errors,
    importando,
    confirmImport,
    importFile,
    backupMsg,
    ultimoBackup,
    fileInputRef,
    valorMinimoNF,
    dataAlvara,
    reservaPct,
    dataPrevisaoTermino,
    savingConf,
    savedConf,
    moduloReset,
    confirmReset,
    resettingProg,
    resetMsg,
    confirmLimpar,
    confirmLimpar2,
    digitouNome,
    limpando,
    setValorMinimoNF,
    setDataAlvara,
    setReservaPct,
    setDataPrevisaoTermino,
    setModuloReset,
    setConfirmReset,
    setConfirmLimpar2,
    setDigitouNome,
    setOrcamentoTotal,
    startEdit,
    cancelEdit,
    setField,
    handleSave,
    handleExportarBackup,
    handleSelecionarImport,
    handleConfirmarImport,
    handleSaveConfiguracoes,
    handleResetarProgresso,
    handleLimparTodos,
    cancelImport,
    openLimpar,
    closeLimparStep1,
    closeLimparStep2,
  }
}

export type ConfigPageState = ReturnType<typeof useConfigPage>
